import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  getAllDocsSnap,
  setDocData,
} from "../../../utils/firebase/Firebase.utils";
import {
  FiCheck,
  FiX,
  FiFileText,
  FiCalendar,
  FiUser,
  FiMessageSquare,
  FiCornerUpLeft,
} from "react-icons/fi";
import { FaMoneyBillWave } from "react-icons/fa";
import Select from "react-select";
import toast from "react-hot-toast";
import { v4 } from "uuid";

function ForApproval() {
  const currentUser = useSelector((state) => state.userObject);
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [comment, setComment] = useState("");
  const [showResubmitModal, setShowResubmitModal] = useState(false);
  const [resubmitComment, setResubmitComment] = useState("");

  useEffect(() => {
    const fetchRequests = async () => {
      await getAllDocsSnap("paymentRequests", (data) => {
        // Filter requests where current user is the approver and status is pending
        const filteredRequests = data.filter(
          (request) =>
            request.approver?.value === currentUser.email &&
            request.status === "pending"
        );
        setRequests(filteredRequests);
        setIsLoading(false);
      });
    };
    fetchRequests();
  }, [currentUser.email]);

  const handleApprove = async (request) => {
    try {
      const updatedRequest = {
        ...request,
        status: "approved",
        approvedAt: new Date().toISOString(),
        approvedBy: currentUser.email,
        approvedByName: currentUser.displayName,
        comments: [
          ...(request.comments || []),
          {
            text: comment,
            type: "approval",
            createdAt: new Date().toISOString(),
            createdBy: currentUser.email,
            createdByName: currentUser.displayName,
          },
        ],
      };
      await setDocData("paymentRequests", request.id, updatedRequest);
      toast.success("Request approved successfully");
      setShowModal(false);
      setComment("");
    } catch (error) {
      console.error("Error approving request:", error);
      toast.error("Error approving request");
    }
  };

  const handleReject = async (request) => {
    try {
      const updatedRequest = {
        ...request,
        status: "rejected",
        rejectedAt: new Date().toISOString(),
        rejectedBy: currentUser.email,
        rejectedByName: currentUser.displayName,
        comments: [
          ...(request.comments || []),
          {
            text: comment,
            type: "rejection",
            createdAt: new Date().toISOString(),
            createdBy: currentUser.email,
            createdByName: currentUser.displayName,
          },
        ],
      };
      await setDocData("paymentRequests", request.id, updatedRequest);
      toast.success("Request rejected successfully");
      setShowModal(false);
      setComment("");
    } catch (error) {
      console.error("Error rejecting request:", error);
      toast.error("Error rejecting request");
    }
  };

  const handleResubmit = async (request) => {
    try {
      const updatedRequest = {
        ...request,
        status: "pending",
        resubmittedAt: new Date().toISOString(),
        resubmittedBy: currentUser.email,
        resubmittedByName: currentUser.displayName,
        comments: [
          ...(request.comments || []),
          {
            text: resubmitComment,
            type: "resubmission",
            createdAt: new Date().toISOString(),
            createdBy: currentUser.email,
            createdByName: currentUser.displayName,
          },
        ],
      };
      await setDocData("paymentRequests", request.id, updatedRequest);
      toast.success("Request resubmitted successfully");
      setShowResubmitModal(false);
      setResubmitComment("");
    } catch (error) {
      console.error("Error resubmitting request:", error);
      toast.error("Error resubmitting request");
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: "bg-[#363062]/10 text-[#363062]",
      approved: "bg-[#8FCD00]/10 text-[#8FCD00]",
      rejected: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status]}`}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getCommentTypeBadge = (type) => {
    const typeStyles = {
      approval: "bg-[#8FCD00]/10 text-[#8FCD00]",
      rejection: "bg-red-100 text-red-800",
      resubmission: "bg-[#363062]/10 text-[#363062]",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${typeStyles[type]}`}
      >
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#363062]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-lg font-semibold text-[#363062]">
            Pending Approvals
          </h1>
          <p className="text-sm text-gray-500">
            Review and approve payment requests
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {requests.length > 0 ? (
                requests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {new Date(request.date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {request.category?.label || "Uncategorized"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {request.supplierName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <FaMoneyBillWave className="text-[#363062]" />
                        <span>₱{request.amount}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowModal(true);
                          }}
                          className="text-[#363062] hover:text-[#363062]/80"
                        >
                          <FiMessageSquare className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-sm text-gray-500"
                  >
                    No pending approvals
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[#363062]">
                Review Payment Request
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedRequest(null);
                  setComment("");
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <p className="text-sm text-gray-900">
                    {new Date(selectedRequest.date).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedRequest.category?.label || "Uncategorized"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supplier
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedRequest.supplierName}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount
                  </label>
                  <div className="flex items-center gap-2">
                    <FaMoneyBillWave className="text-[#363062]" />
                    <p className="text-sm text-gray-900">
                      ₱{selectedRequest.amount}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    VAT
                  </label>
                  <div className="flex items-center gap-2">
                    <FaMoneyBillWave className="text-[#363062]" />
                    <p className="text-sm text-gray-900">
                      ₱{selectedRequest.vat}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  {getStatusBadge(selectedRequest.status)}
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedRequest.description}
                  </p>
                </div>
                {selectedRequest.invoiceFiles &&
                  selectedRequest.invoiceFiles.length > 0 && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Invoices
                      </label>
                      <div className="space-y-2">
                        {selectedRequest.invoiceFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-gray-50 p-2 rounded-lg"
                          >
                            <div className="flex items-center gap-2">
                              <FiFileText className="w-4 h-4 text-[#363062]" />
                              <span className="text-sm text-gray-900 truncate max-w-xs">
                                {file}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <a
                                href={file}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#363062] hover:text-[#363062]/80"
                              >
                                <FiMessageSquare className="w-4 h-4" />
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Comment
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#363062]/20 focus:border-[#363062]"
                    rows="3"
                    placeholder="Add your comment here..."
                  />
                </div>

                {/* Comments History */}
                {selectedRequest.comments &&
                  selectedRequest.comments.length > 0 && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Comments History
                      </label>
                      <div className="space-y-3">
                        {selectedRequest.comments.map((comment, index) => (
                          <div
                            key={index}
                            className="bg-gray-50 p-3 rounded-lg space-y-2"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900">
                                  {comment.createdByName}
                                </span>
                                {getCommentTypeBadge(comment.type)}
                              </div>
                              <span className="text-xs text-gray-500">
                                {new Date(comment.createdAt).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700">
                              {comment.text}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
              <div className="flex justify-end gap-4 mt-6">
                <button
                  onClick={() => handleReject(selectedRequest)}
                  className="px-4 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-50"
                >
                  Reject
                </button>
                <button
                  onClick={() => handleApprove(selectedRequest)}
                  className="px-4 py-2 bg-[#8FCD00] text-white rounded-lg hover:bg-[#8FCD00]/90"
                >
                  Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resubmit Modal */}
      {showResubmitModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[#363062]">
                Resubmit Request
              </h2>
              <button
                onClick={() => {
                  setShowResubmitModal(false);
                  setSelectedRequest(null);
                  setResubmitComment("");
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Response to Previous Comments
                </label>
                <textarea
                  value={resubmitComment}
                  onChange={(e) => setResubmitComment(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#363062]/20 focus:border-[#363062]"
                  rows="3"
                  placeholder="Add your response here..."
                />
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  onClick={() => {
                    setShowResubmitModal(false);
                    setResubmitComment("");
                  }}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleResubmit(selectedRequest)}
                  className="px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-[#363062]/90"
                >
                  Resubmit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ForApproval;
